// Re-authored from ../design-system/components/feedback/Toast.jsx.
// Single presentational notification card — render inside a fixed stack you control.
// NOT a provider/timer; the consumer owns lifetime and positioning.
//
// A11y note: role="status" (polite) is the default for all kinds. For kind="error"
// callers should pass role="alert" (assertive) — {...rest} is spread after the default
// so it will override: <Toast kind="error" role="alert" ... />

import { type HTMLAttributes, type ReactNode } from 'react';
import { CircleCheck, CircleAlert, TriangleAlert, Info, Bell, X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ToastProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  kind?: 'success' | 'error' | 'warning' | 'info' | 'default';
  title?: ReactNode;
  message?: ReactNode;
  onClose?: () => void;
}

const KIND: Record<
  NonNullable<ToastProps['kind']>,
  { accent: string; Icon: LucideIcon }
> = {
  success: { accent: 'var(--ollio-success)', Icon: CircleCheck },
  error:   { accent: 'var(--ollio-danger)',  Icon: CircleAlert },
  warning: { accent: 'var(--ollio-warning)', Icon: TriangleAlert },
  info:    { accent: 'var(--ollio-info)',    Icon: Info },
  default: { accent: 'var(--ollio-navy)',   Icon: Bell },
};

export function Toast({
  kind = 'default',
  title,
  message,
  onClose,
  className,
  style,
  ...rest
}: ToastProps) {
  const { accent, Icon } = KIND[kind] ?? KIND.default;

  return (
    <div
      role="status"
      data-kind={kind}
      className={cn(
        'flex items-start gap-3',
        'bg-card border border-[var(--border-soft)] rounded-[var(--radius-card)]',
        'shadow-[var(--shadow-lg)] p-4 min-w-[300px] max-w-[420px]',
        'border-l-[3px]',
        className,
      )}
      style={{ borderLeftColor: accent, ...style }}
      {...rest}
    >
      {/* Icon */}
      <span
        className="flex-none w-[22px] h-[22px] mt-px flex items-center justify-center [&_svg]:h-[19px] [&_svg]:w-[19px]"
        style={{ color: accent }}
        aria-hidden="true"
      >
        <Icon />
      </span>

      {/* Body */}
      <div className="flex-1 flex flex-col gap-0.5">
        {title != null && (
          <span className="text-sm font-semibold text-[var(--text-heading)]">{title}</span>
        )}
        {message != null && (
          <span className="text-sm text-muted leading-snug">{message}</span>
        )}
      </div>

      {/* Dismiss button */}
      {onClose != null && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onClose}
          className={cn(
            'flex-none text-muted',
            'hover:text-[var(--text-heading)] hover:bg-[var(--ollio-navy-light)]',
            'rounded p-0.5 transition-colors',
          )}
        >
          <X className="h-[15px] w-[15px]" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
